const FAQ_NAV_RESUME_BUFFER = 16;
const FAQ_MOBILE_MEDIA_QUERY =
  "(max-width: 767px), (max-width: 1024px) and (orientation: portrait)";

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".faq-page").forEach((faqSection) => {
    initFaqPageNav(faqSection);
  });
});

function initFaqPageNav(faqSection) {
  syncStickyOffset(faqSection);
  initMobileSticky(faqSection);

  const navLinks = Array.from(
    faqSection.querySelectorAll(".er-cat-nav .er-pill[href^='#cat-']")
  );
  const quickLinks = Array.from(
    faqSection.querySelectorAll(".er-quick-card[href^='#cat-']")
  );
  const sectionHeadings = Array.from(
    faqSection.querySelectorAll(".er-cat-label[id]")
  );

  if (!navLinks.length || !sectionHeadings.length) {
    return;
  }

  const linkMap = new Map(
    navLinks.map((link) => [normalizeHash(link.getAttribute("href")), link])
  );
  const headingMap = new Map(
    sectionHeadings.map((heading) => [`#${heading.id}`, heading])
  );
  let isObserverPaused = false;
  let pendingHeading = null;
  let pendingHash = "";
  let resumeCheckFrameId = null;

  const setActiveLink = (hash) => {
    const normalizedHash = normalizeHash(hash);

    if (!normalizedHash) {
      return;
    }

    navLinks.forEach((link) => {
      link.classList.toggle(
        "is-active",
        normalizeHash(link.getAttribute("href")) === normalizedHash
      );
    });
  };

  const setActiveFromHeading = (heading) => {
    if (!heading || !heading.id) {
      return;
    }

    setActiveLink(`#${heading.id}`);
  };

  const clearPendingNavigation = () => {
    isObserverPaused = false;
    pendingHeading = null;
    pendingHash = "";
  };
  const getResumeOffset = () => getStickyOffset() + FAQ_NAV_RESUME_BUFFER;

  const maybeResumeObserver = () => {
    if (!isObserverPaused || !pendingHeading) {
      return;
    }

    if (!document.body.contains(pendingHeading)) {
      clearPendingNavigation();
      return;
    }

    const resumeOffset = getResumeOffset();
    const headingTop = pendingHeading.getBoundingClientRect().top;
    const reachedResumePoint =
      Math.abs(headingTop - resumeOffset) <= FAQ_NAV_RESUME_BUFFER;

    if (reachedResumePoint) {
      const targetHeading = pendingHeading;

      clearPendingNavigation();
      setActiveFromHeading(targetHeading);
    }
  };

  const queueResumeCheck = () => {
    if (resumeCheckFrameId !== null) {
      return;
    }

    resumeCheckFrameId = window.requestAnimationFrame(() => {
      resumeCheckFrameId = null;
      syncStickyOffset(faqSection);
      maybeResumeObserver();
    });
  };

  const startProgrammaticNavigation = (hash) => {
    const normalizedHash = normalizeHash(hash);
    const targetHeading = headingMap.get(normalizedHash) || null;

    setActiveLink(normalizedHash);

    if (!targetHeading) {
      clearPendingNavigation();
      return;
    }

    isObserverPaused = true;
    pendingHeading = targetHeading;
    pendingHash = normalizedHash;
    queueResumeCheck();
  };

  navLinks.forEach((link) => {
    link.addEventListener("click", () => {
      startProgrammaticNavigation(link.getAttribute("href"));
    });
  });

  quickLinks.forEach((link) => {
    link.addEventListener("click", () => {
      startProgrammaticNavigation(link.getAttribute("href"));
    });
  });

  const observer = new IntersectionObserver(
    (entries) => {
      if (isObserverPaused) {
        maybeResumeObserver();
        return;
      }

      const visibleEntries = entries
        .filter((entry) => entry.isIntersecting && linkMap.has(`#${entry.target.id}`))
        .sort((entryA, entryB) => entryA.boundingClientRect.top - entryB.boundingClientRect.top);

      if (visibleEntries.length) {
        setActiveFromHeading(visibleEntries[0].target);
      }
    },
    {
      rootMargin: "-20% 0px -65% 0px",
      threshold: [0.2, 0.5, 1]
    }
  );

  sectionHeadings.forEach((heading) => {
    if (linkMap.has(`#${heading.id}`)) {
      observer.observe(heading);
    }
  });

  const initialHash = normalizeHash(window.location.hash);

  if (initialHash && linkMap.has(initialHash)) {
    setActiveLink(initialHash);
  } else {
    setActiveFromHeading(sectionHeadings[0]);
  }

  window.addEventListener("hashchange", () => {
    const nextHash = normalizeHash(window.location.hash);

    if (isObserverPaused && nextHash && nextHash !== pendingHash) {
      clearPendingNavigation();
    }

    if (linkMap.has(nextHash)) {
      setActiveLink(nextHash);
    }
  });

  window.addEventListener("resize", () => {
    syncStickyOffset(faqSection);
    maybeResumeObserver();
  });

  window.addEventListener(
    "scroll",
    () => {
      syncStickyOffset(faqSection);
      maybeResumeObserver();
    },
    { passive: true }
  );
}

function normalizeHash(value) {
  if (!value || typeof value !== "string") {
    return "";
  }

  return value.startsWith("#") ? value : `#${value}`;
}

function getStickyOffset() {
  const siteHeader =
    document.getElementById("shopify-section-header") ||
    document.querySelector("header");

  if (!siteHeader) {
    return 0;
  }

  return Math.max(siteHeader.getBoundingClientRect().bottom, 0);
}

function syncStickyOffset(faqSection) {
  faqSection.style.setProperty(
    "--faq-sticky-offset",
    `${Math.round(getStickyOffset())}px`
  );
}

function initMobileSticky(faqSection) {
  const nav = faqSection.querySelector(".er-cat-nav");

  if (!nav) {
    return;
  }

  const mobileMediaQuery = window.matchMedia(FAQ_MOBILE_MEDIA_QUERY);
  const navPlaceholder = document.createElement("div");
  let frameId = null;

  navPlaceholder.className = "er-cat-nav-placeholder";
  nav.before(navPlaceholder);

  const resetFixedNav = () => {
    nav.classList.remove("is-fixed");
    navPlaceholder.style.display = "none";
    navPlaceholder.style.height = "0px";
  };

  const updateFixedNavState = () => {
    frameId = null;
    syncStickyOffset(faqSection);

    if (!mobileMediaQuery.matches) {
      resetFixedNav();
      return;
    }

    const headerOffset = getStickyOffset();
    const navHeight = nav.offsetHeight;
    const triggerElement = nav.classList.contains("is-fixed") ? navPlaceholder : nav;
    const triggerTop = triggerElement.getBoundingClientRect().top;
    const sectionBottom = faqSection.getBoundingClientRect().bottom;
    const shouldFix =
      triggerTop <= headerOffset && sectionBottom > headerOffset + navHeight;

    if (!shouldFix) {
      resetFixedNav();
      return;
    }

    navPlaceholder.style.display = "block";
    navPlaceholder.style.height = `${navHeight}px`;
    nav.classList.add("is-fixed");
  };

  const queueFixedNavUpdate = () => {
    if (frameId !== null) {
      return;
    }

    frameId = window.requestAnimationFrame(updateFixedNavState);
  };

  const handleMediaChange = () => {
    queueFixedNavUpdate();
  };

  if (typeof mobileMediaQuery.addEventListener === "function") {
    mobileMediaQuery.addEventListener("change", handleMediaChange);
  } else {
    mobileMediaQuery.onchange = handleMediaChange;
  }

  window.addEventListener(
    "scroll",
    () => {
      queueFixedNavUpdate();
    },
    { passive: true }
  );

  window.addEventListener("resize", () => {
    queueFixedNavUpdate();
  });

  queueFixedNavUpdate();
}
